/**
 * "Allow Claude to work in Ferma Hub as you" — the consent step of connecting a Claude (ported from
 * Device Lab, 2026-10-08: each person connects their own Claude with one click, no key to copy).
 *
 * Claude sends the person here from the service's `/oauth/authorize`. What is shown is **who asks** and
 * **where the answer goes** — the redirect's host, which the MCP authorization spec requires a consent
 * screen to display, and a plain word for Claude Code's loopback address. Allow returns them to Claude
 * with a one-time code; the service turns it into their personal key.
 *
 * Standalone, not inside the workspace shell: it opens in a tab Claude made, and the sidebar would
 * offer a whole application to somebody who came to press one button. With no session, `main.tsx`
 * shows the usual sign-in first and comes back here (the `req` stays in the URL).
 */

import { Sprout } from 'lucide-react'
import { useEffect, useState } from 'react'

import type { TikTokHubApi } from '../../api/client'
import { ApiError } from '../../api/client'
import type { OAuthRequest } from '../../api/types'
import { useT } from '../../i18n'

export function Consent({ api, requestId }: { api: TikTokHubApi; requestId: string }) {
  const t = useT()
  const o = t.oauth
  const [req, setReq] = useState<OAuthRequest | null>(null)
  const [gone, setGone] = useState(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const failed = (e: unknown) => {
    if (e instanceof ApiError && e.status === 404) setGone(true)
    else setProblem(e instanceof Error ? e.message : String(e))
  }

  useEffect(() => {
    void api.oauthRequest(requestId).then(setReq).catch(failed)
  }, [api, requestId])

  const answer = async (allow: boolean) => {
    setBusy(true)
    try {
      const { redirect } = await api.oauthAnswer(requestId, allow)
      window.location.assign(redirect)
    } catch (e) {
      setBusy(false)
      failed(e)
    }
  }

  return (
    <div className="page" style={{ placeContent: 'center', minHeight: '100vh' }}>
      <section className="card" style={{ maxWidth: 520, margin: '0 auto', width: '100%' }}>
        <span className="brand" style={{ fontSize: 19 }}>
          <span className="mark"><Sprout className="ico" size={15} aria-hidden /></span>
          {o.title}
        </span>
        <div className="consent-body">
          {gone && <p>{o.gone}</p>}
          {problem && <p className="bad-text">{problem}</p>}
          {!req && !gone && !problem && <p className="dim">…</p>}
          {req && !gone && (
            <>
              <p className="consent-asks">{o.asks(req.client_name)}</p>
              <p className="small">
                {o.goesTo} <b className="mono">{req.loopback ? o.loopback : req.redirect_host}</b>
              </p>
              <p className="small">{o.can}</p>
              <p className="small dim">{o.cannot}</p>
              <p className="tiny dim">{o.revokeLater}</p>
              <div className="row" style={{ justifyContent: 'flex-end' }}>
                <button className="ghost" disabled={busy} onClick={() => void answer(false)}>{o.deny}</button>
                <button className="primary" disabled={busy} onClick={() => void answer(true)}>
                  {busy ? o.working : o.allow}
                </button>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  )
}
